/**
 * Bundle identifier validation.
 *
 * Apple bundle identifiers (CFBundleIdentifier) follow a reverse-DNS form,
 * e.g. `com.example.MyApp`. The official rules are:
 *  - Must contain only alphanumeric characters (A-Z, a-z, 0-9), hyphens (-),
 *    and periods (.).
 *  - Must contain at least one period (i.e. at least two segments).
 *  - Each dot-separated segment must be non-empty.
 *  - Segments cannot start or end with a hyphen.
 *
 * We intentionally keep this client-side validation strict-but-friendly:
 * it surfaces obviously malformed identifiers before we make a network call
 * to fetch a PPPC policy keyed by bundle id, but it does not try to
 * replicate every server-side rule.
 */

/** Maximum length we'll accept; well above any real-world bundle id. */
export const MAX_BUNDLE_ID_LENGTH = 255;

/** Minimum length: shortest plausible reverse-DNS, e.g. "a.b". */
export const MIN_BUNDLE_ID_LENGTH = 3;

const SEGMENT_PATTERN = /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/;

export type BundleIdValidationReason =
  | "empty"
  | "not-string"
  | "too-short"
  | "too-long"
  | "invalid-characters"
  | "missing-dot"
  | "empty-segment"
  | "invalid-segment";

export interface BundleIdValidationResult {
  valid: boolean;
  /** Trimmed value used for validation; useful for callers that want to pass the cleaned id along. */
  normalized: string;
  /** Stable machine-readable reason, present when `valid === false`. */
  reason?: BundleIdValidationReason;
  /** Human-readable message suitable for surfacing in UI. */
  message?: string;
}

/**
 * Validate a bundle identifier against the reverse-DNS rules above.
 * Returns a structured result so callers can render specific warnings.
 */
export function validateBundleId(value: unknown): BundleIdValidationResult {
  if (typeof value !== "string") {
    return {
      valid: false,
      normalized: "",
      reason: "not-string",
      message: "Bundle ID must be a text value.",
    };
  }

  const normalized = value.trim();

  if (normalized.length === 0) {
    return {
      valid: false,
      normalized,
      reason: "empty",
      message: "Bundle ID is empty.",
    };
  }

  if (normalized.length < MIN_BUNDLE_ID_LENGTH) {
    return {
      valid: false,
      normalized,
      reason: "too-short",
      message: `Bundle ID is too short (minimum ${MIN_BUNDLE_ID_LENGTH} characters).`,
    };
  }

  if (normalized.length > MAX_BUNDLE_ID_LENGTH) {
    return {
      valid: false,
      normalized,
      reason: "too-long",
      message: `Bundle ID is too long (maximum ${MAX_BUNDLE_ID_LENGTH} characters).`,
    };
  }

  if (!/^[A-Za-z0-9.\-]+$/.test(normalized)) {
    return {
      valid: false,
      normalized,
      reason: "invalid-characters",
      message:
        "Bundle ID may only contain letters, numbers, hyphens, and periods (e.g. com.example.app).",
    };
  }

  if (!normalized.includes(".")) {
    return {
      valid: false,
      normalized,
      reason: "missing-dot",
      message:
        "Bundle ID must use reverse-DNS format with at least one period (e.g. com.example.app).",
    };
  }

  const segments = normalized.split(".");
  for (const segment of segments) {
    if (segment.length === 0) {
      return {
        valid: false,
        normalized,
        reason: "empty-segment",
        message:
          "Bundle ID cannot contain empty segments (e.g. consecutive periods or a leading/trailing dot).",
      };
    }
    if (!SEGMENT_PATTERN.test(segment)) {
      return {
        valid: false,
        normalized,
        reason: "invalid-segment",
        message:
          "Each segment of the Bundle ID must start and end with a letter or number (e.g. com.example.my-app).",
      };
    }
  }

  return { valid: true, normalized };
}

/** Convenience boolean check when you don't need the failure reason. */
export function isValidBundleId(value: unknown): boolean {
  return validateBundleId(value).valid;
}
